import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useState } from 'react';
import { Deploy } from './components';
import { Button } from '~/components/ui/Button';
import { Dropdown } from '~/components/ui/Dropdown';
import { IconButton } from '~/components/ui/IconButton';
import { UpgradePlanDialogs } from '~/components/ui/pricing/UpgradePlanDialogs';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { chatHooks } from '~/lib/chat-hooks';
import { triggerDeploy } from '~/lib/deploy';
import { importStore } from '~/lib/projects';
import { domainsStore } from '~/lib/stores/domains';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import { HELP_CENTER_HOSTING } from '~/lib/urls';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

const APP_STORE_DOCS_URL = 'https://expo.dev/ai/bolt?utm_source=bolt&utm_medium=partner&utm_campaign=13932866-Nurture';

export const PublishMenu = () => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isCustomDomainDialogOpen, setIsCustomDomainDialogOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const slug = useStore(projectStore.getSlugStore());
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const permissions = useStore(projectStore.permissions);
  const framework = useStore(workbenchStore.framework);
  const { deploymentInfo } = useStore(importStore);
  const isDeploying = useStore(chatHooks.isDeploying);

  const isPaidUser = useStore(userStore.isPaidUser);
  const isExpo = framework?.name === 'expo';

  // modify this to customize the experience for nativescript
  const supportsDeployAppStore = isExpo;
  const disabled = !slug;

  const openBuyDomainModal = useCallback(() => {
    domainsStore.openBuyNewDomain();

    setIsCustomDomainDialogOpen(false);
    setIsDropdownVisible(false);
  }, []);

  const openOwnDomainModal = useCallback(() => {
    domainsStore.openAddCustomDomain();

    setIsCustomDomainDialogOpen(false);
    setIsDropdownVisible(false);
  }, []);

  const openManageCustomDomain = useCallback(() => {
    domainsStore.openManageDomains();

    setIsDropdownVisible(false);
  }, []);

  const onToggleDropdown = useCallback(() => {
    setIsDropdownVisible((value) => {
      if (!value) {
        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.SiteDeployDialogOpen,
            properties: {
              intent: deploymentInfo ? 'update' : 'publish',
            },
          },
        });
      }

      return !value;
    });
  }, [deploymentInfo]);

  const handleDomainAction = (action: 'buy' | 'connect') => {
    if (!isPaidUser) {
      setIsUpgradeModalOpen(true);
      return;
    }

    if (action === 'buy') {
      openBuyDomainModal();
    } else if (action === 'connect') {
      openOwnDomainModal();
    }
  };

  return (
    <DropdownMenu.Root open={isDropdownVisible} onOpenChange={onToggleDropdown}>
      <Deploy.TriggerButton disabled={disabled} aria-controls="publish-menu" />

      <Deploy.Popup open={isDropdownVisible} id="publish-menu">
        <p className="font-medium text-sm">Publish your project</p>

        <div className="flex flex-col">
          <Deploy.DeploymentInfo isDeploying={isDeploying} onManageCustomDomain={openManageCustomDomain} />
          <Deploy.DeploymentTime />
          {/* <Deploy.DeploymentStatus isDeploying={isDeploying} /> */}
        </div>

        <Deploy.SocialShare />

        <hr className="border-bolt-ds-borderOutline" />

        {deploymentInfo?.kind === 'static-hosting' && !deploymentInfo?.is_custom_domain && (
          <>
            <Dropdown label="Custom domain" open={isCustomDomainDialogOpen} onOpenChange={setIsCustomDomainDialogOpen}>
              <Dropdown.Item
                className="text-center"
                onClick={() => {
                  handleDomainAction('buy');
                }}
              >
                Buy a new domain
              </Dropdown.Item>
              <Dropdown.Item
                className="text-center"
                onClick={() => {
                  handleDomainAction('connect');
                }}
              >
                Add a domain you own
              </Dropdown.Item>
            </Dropdown>
            <UpgradePlanDialogs
              isOpen={isUpgradeModalOpen}
              cards={[!activeOrganization ? 'pro' : 'teams']}
              onClose={() => setIsUpgradeModalOpen(false)}
              message={`Attaching custom domains to your site is a ${!activeOrganization ? 'Pro' : 'Teams'} feature, upgrade to ${!activeOrganization ? 'Pro' : 'Teams'} to unlock it.`}
            />
          </>
        )}

        <div className="flex items-center gap-2">
          <a href={HELP_CENTER_HOSTING} target="_blank">
            <IconButton title="Need help?" variant="base" size="sm" icon="i-ph:question" />
          </a>

          {supportsDeployAppStore && <div className="text-xs text-bolt-ds-textTertiary pl-2">Publish on:</div>}

          <Button
            disabled={!permissions.canPublish || isDeploying}
            variant="primary"
            loading={isDeploying}
            size="sm"
            onClick={triggerDeploy}
            className="flex-1"
            title={!permissions.canPublish ? 'You do not have permission to publish' : undefined}
          >
            {supportsDeployAppStore ? 'Web' : deploymentInfo ? 'Update' : 'Publish'}
          </Button>

          {supportsDeployAppStore && (
            <Button
              disabled={!permissions.canPublish || isDeploying}
              variant="primary"
              size="sm"
              className="flex-1"
              asChild
            >
              <a href={APP_STORE_DOCS_URL} target="_blank" className="flex">
                App Store
              </a>
            </Button>
          )}
        </div>
      </Deploy.Popup>
    </DropdownMenu.Root>
  );
};
